import { useEffect, useState } from "react";
import {
  AnalysisPayload,
  CenterLinePoint,
  SavedAnalysis,
  ThermoHeightReferenceLine,
  ThermoStats
} from "../types";
import { clearCache, deleteAnalysis, loadAnalyses, saveAnalysis } from "../api/postgrest";
import ConfirmDialog from "./ConfirmDialog";
import { formatAppDateTime } from "../utils/dateTime";

type Props = {
  selectedIds: number[];
  meanCenterLine: CenterLinePoint[];
  stats: ThermoStats;
  bottleStats: ThermoStats;
  heightReferenceSampleId?: number | null;
  heightReferenceLine?: ThermoHeightReferenceLine | null;
  heightReferenceHeightMm?: number | null;
  refreshNonce?: number;
  onRestore: (analysis: SavedAnalysis) => void;
};

export default function AnalysisPanel({
  selectedIds,
  meanCenterLine,
  stats,
  bottleStats,
  heightReferenceSampleId,
  heightReferenceLine,
  heightReferenceHeightMm,
  refreshNonce,
  onRestore
}: Props) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<SavedAnalysis[]>([]);
  const [pendingDelete, setPendingDelete] = useState<SavedAnalysis | null>(null);

  async function refreshAnalyses() {
    try {
      clearCache();
      const analyses = await loadAnalyses();
      setSaved(analyses);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load analyses");
    }
  }

  const handleDelete = async (id: number) => {
    setBusy(true);
    setError(null);
    try {
      await deleteAnalysis(id);
      await refreshAnalyses();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete analysis");
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!pendingDelete) return;
    await handleDelete(pendingDelete.id);
    setPendingDelete(null);
  };

  useEffect(() => {
    refreshAnalyses();
  }, [refreshNonce]);

  const handleSave = async () => {
    if (!name || !selectedIds.length) return;
    setBusy(true);
    setError(null);
    const payload: AnalysisPayload = {
      name,
      sample_id_array: selectedIds,
      mean_center_lines: meanCenterLine,
      min_temperature: stats.minTemperature,
      mean_temperature: stats.meanTemperature,
      max_temperature: stats.maxTemperature,
      min_viscosity: stats.minViscosity,
      mean_viscosity: stats.meanViscosity,
      max_viscosity: stats.maxViscosity,
      min_bottle_temperature: bottleStats.minTemperature,
      mean_bottle_temperature: bottleStats.meanTemperature,
      max_bottle_temperature: bottleStats.maxTemperature,
      min_bottle_viscosity: bottleStats.minViscosity,
      mean_bottle_viscosity: bottleStats.meanViscosity,
      max_bottle_viscosity: bottleStats.maxViscosity,
      height_reference_sample_id: heightReferenceSampleId ?? null,
      height_reference_line: heightReferenceLine ?? null,
      height_reference_height_mm: heightReferenceHeightMm ?? null
    };
    try {
      await saveAnalysis(payload);
      setName("");
      await refreshAnalyses();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save analysis");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3 className="section-title">Save / Load Analysis</h3>
        <span className="pill">Selected IDs: {selectedIds.join(", ") || "none"}</span>
      </div>
      <div style={{ marginTop: 8, display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 240px" }}>
          <div className="label">Analysis Name</div>
          <input
            className="input"
            placeholder="e.g. Line change 12/02"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
          <button className="button" disabled={!selectedIds.length || busy} onClick={handleSave}>
            Save Analysis
          </button>
          {busy && <span className="label">Saving…</span>}
        </div>
      </div>
      {error && <div className="status-error">{error}</div>}

      <div className="divider" />

      {saved.length ? (
        <div className="table-scroll">
          <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Samples</th>
              <th>Saved At</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {saved.map((analysis) => (
              <tr key={analysis.id}>
                <td>{analysis.name}</td>
                <td className="text-wrap-anywhere" style={{ maxWidth: "min(300px, 60vw)" }}>
                  {analysis.sample_id_array.join(", ")}
                </td>
                <td>{formatAppDateTime(analysis.timestamp)}</td>
                <td style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <button className="button secondary" onClick={() => onRestore(analysis)}>
                    Load
                  </button>
                  <button
                    className="button secondary"
                    onClick={() => setPendingDelete(analysis)}
                    disabled={busy}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
          </table>
        </div>
      ) : (
        <div style={{ color: "var(--text-faint)" }}>No analyses saved yet.</div>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        title="Delete analysis?"
        message={
          pendingDelete
            ? `Are you sure you want to delete \"${pendingDelete.name}\"? This cannot be undone.`
            : "Are you sure you want to delete this analysis? This cannot be undone."
        }
        confirmLabel="Delete"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setPendingDelete(null)}
        busy={busy}
      />
    </div>
  );
}
